import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FileText, Search, Loader2, AlertTriangle, Upload, Wand2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ML_SAFE_STYLE } from '../utils/mlSafe';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Script PDF Viewer with scene-jump navigation.
 *
 * Left panel: searchable scene list. Each row = scene number + heading.
 * Click a scene → iframe reloads with `#page=N&v=…` so the browser PDF viewer
 * scrolls to that page (based on PyPDF2-extracted page→scene index built on
 * the backend).
 */
export default function ScriptPdfPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [items, setItems]       = useState([]);
  const [totalPages, setTotal]  = useState(0);
  const [activeScene, setActive] = useState(null);    // scene_id
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [pdfBlob, setPdfBlob]   = useState(null);     // ObjectURL
  const [pdfError, setPdfError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [demoKey, setDemoKey]   = useState(null);     // 'english' | 'hindi' | 'malayalam' | null
  const [recovering, setRecovering] = useState(false);
  const [recoverResult, setRecoverResult] = useState(null); // last-run summary
  const [showRecoverConfirm, setShowRecoverConfirm] = useState(false);
  const iframeRef = useRef(null);

  // Fetch project so we can enrich the empty-state copy for demos.
  // Non-fatal: any error falls back to the generic empty state.
  useEffect(() => {
    if (!projectId || !token) return;
    axios.get(`${API}/projects/${projectId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        if (r.data?.is_demo) setDemoKey(r.data?.demo_key || null);
      })
      .catch(() => {});
  }, [projectId, token]);

  // 1. Load scene index
  useEffect(() => {
    if (!projectId || !token) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/projects/${projectId}/script/scenes-pages`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setItems(r.data?.items || []);
        setTotal(r.data?.total_pages || 0);
        if ((r.data?.items || []).length) setActive(r.data.items[0].scene_id);
      } catch (e) {
        // Non-fatal — the PDF can still be shown without the index
        console.error('script-pdf.scene-index-fetch:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, token]);

  // 2. Fetch the PDF as a blob (so we can attach the auth header)
  useEffect(() => {
    if (!projectId || !token) return;
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/projects/${projectId}/script/pdf`,
          { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setPdfBlob(objectUrl);
      } catch (e) {
        const status = e?.response?.status;
        let msg;
        if (status === 404 && demoKey === 'malayalam') {
          msg = 'Demo language: മലയാളം. Upload your Malayalam screenplay to view it here — per-scene screenplay text is already loaded in Master Breakdown.';
        } else if (status === 404) {
          msg = 'The original PDF for this project is not available. Re-upload the script to enable the PDF view.';
        } else {
          msg = 'Failed to load script PDF.';
        }
        if (!cancelled) setPdfError(msg);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [projectId, token, demoKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      String(it.scene_number ?? '').includes(q) ||
      (it.scene_header || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const jumpToScene = (it) => {
    setActive(it.scene_id);
    const page = it.page || 1;
    setCurrentPage(page);
    // Force the <iframe> to fully remount via the `key` prop below — simply
    // mutating .src to the same blob URL with a new #page= fragment is NOT
    // honoured by Chrome/Edge once the PDF is already loaded.
  };

  const runRecovery = async () => {
    setShowRecoverConfirm(false);
    setRecovering(true);
    setRecoverResult(null);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/scenes/backfill-raw-text`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecoverResult(r.data || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Recovery failed';
      setRecoverResult({ error: msg });
    } finally {
      setRecovering(false);
    }
  };

  return (
    <div className="h-[calc(100vh-0px)] flex flex-col" style={{ background: 'var(--cb-bg)' }} data-testid="script-pdf-page">
      <div className="px-6 py-4 border-b border-[#1a1a1a] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-[#e8c547]" />
          <h1 className="text-base font-semibold text-[#f0f0f0]">Script</h1>
          <span className="text-xs text-[#666]">
            {items.length} scene{items.length === 1 ? '' : 's'}
            {totalPages > 0 && ` · ${totalPages} page${totalPages === 1 ? '' : 's'}`}
            {currentPage > 1 && ` · viewing page ${currentPage}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {recoverResult && !recoverResult.error && (
            <span className="text-[10px] text-[#7ac97a]" data-testid="script-recover-summary">
              Slice {recoverResult.text_slice_wrote ?? 0} · Vision {recoverResult.vision_dispatched ?? 0} · Filled {recoverResult.already_populated ?? 0} · None {recoverResult.no_source ?? 0}
            </span>
          )}
          {recoverResult?.error && (
            <span className="text-[10px] text-red-400" data-testid="script-recover-error">
              {String(recoverResult.error).slice(0, 80)}
            </span>
          )}
          <Button
            onClick={() => setShowRecoverConfirm(true)}
            disabled={recovering}
            className="bg-[#1a1a1a] hover:bg-[#252525] text-[#e8c547] text-[11px] border border-[#e8c547]/30 h-7 px-2.5"
            data-testid="script-recover-missing-btn"
            title="Recover missing per-scene script text"
          >
            {recovering ? (
              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Recovering…</>
            ) : (
              <><Wand2 className="h-3 w-3 mr-1" /> Recover missing script text</>
            )}
          </Button>
        </div>
      </div>

      {showRecoverConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          data-testid="script-recover-confirm-modal"
        >
          <div className="bg-[#151515] border border-[#2a2a2a] rounded-lg p-5 max-w-md w-full mx-4">
            <h3 className="text-sm font-semibold text-[#f0f0f0] mb-2">
              Recover missing script text
            </h3>
            <p className="text-xs text-[#a0a0a0] leading-relaxed">
              This scans every scene in this project for missing original screenplay text.
              Free deterministic extraction runs first; it only falls back to AI vision on
              scenes where a PDF is available and no extractable text exists. Existing text
              is never overwritten.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                onClick={() => setShowRecoverConfirm(false)}
                className="bg-transparent hover:bg-[#1a1a1a] text-[#a0a0a0] text-xs border border-[#2a2a2a] h-7 px-3"
                data-testid="script-recover-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={runRecovery}
                className="bg-[#e8c547] hover:bg-[#d4b13d] text-[#0d0d0d] text-xs h-7 px-3"
                data-testid="script-recover-confirm"
              >
                Run recovery
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* Scene list */}
        <aside className="w-72 border-r border-[#1a1a1a] flex flex-col min-h-0" data-testid="script-scene-list">
          <div className="p-3 border-b border-[#1a1a1a]">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-[#666]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search scenes…"
                className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-md text-xs text-[#f0f0f0] pl-8 pr-2 py-1.5 focus:outline-none focus:border-[#e8c547]/40"
                data-testid="script-scene-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-4 w-4 animate-spin text-[#e8c547]" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-6 text-xs text-[#666]">No scenes</div>
            ) : (
              filtered.map(it => (
                <button
                  key={it.scene_id}
                  onClick={() => jumpToScene(it)}
                  className={`w-full text-left px-3 py-2 border-b border-[#1a1a1a] transition-colors ${
                    activeScene === it.scene_id ? 'bg-[#e8c547]/10' : 'hover:bg-[#1a1a1a]'
                  }`}
                  style={ML_SAFE_STYLE}
                  data-testid={`script-scene-jump-${it.scene_number ?? 'unnumbered'}`}
                  title={it.scene_header}
                >
                  <div className="flex items-baseline gap-2">
                    <span className={`text-[10px] font-mono shrink-0 ${
                      activeScene === it.scene_id ? 'text-[#e8c547]' : 'text-[#777]'
                    }`}>
                      {formatSceneNumber(it.scene_number, { prefix: '#' })}
                    </span>
                    <span className={`text-xs truncate ${
                      activeScene === it.scene_id ? 'text-[#f0f0f0]' : 'text-[#a0a0a0]'
                    }`} style={{ letterSpacing: 0 }}>
                      {it.scene_header || '—'}
                    </span>
                  </div>
                  <div className="text-[10px] text-[#555] mt-0.5 pl-7">page {it.page || 1}</div>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* PDF viewer */}
        <main className="flex-1 min-h-0">
          {pdfError ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8" data-testid="script-pdf-error">
              <AlertTriangle className="h-10 w-10 text-amber-400/60 mb-3" />
              <p className="text-sm text-[#a0a0a0] max-w-md mb-4">{pdfError}</p>
              <Button
                onClick={() => navigate(`/projects/${projectId}/upload`)}
                className="bg-[#e8c547] hover:bg-[#d4b13d] text-[#0d0d0d] text-xs"
                data-testid="script-pdf-upload-cta"
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                Upload Script PDF
              </Button>
            </div>
          ) : !pdfBlob ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#e8c547]" />
            </div>
          ) : (
            <iframe
              key={`pdf-${currentPage}`}
              ref={iframeRef}
              title="script-pdf"
              src={`${pdfBlob}#page=${currentPage}&zoom=page-width&navpanes=0`}
              className="w-full h-full border-0 bg-[#0d0d0d]"
              data-testid="script-pdf-iframe"
            />
          )}
        </main>
      </div>
    </div>
  );
}
